import React, { useState, useCallback, useEffect, useRef } from 'react';

const PERCENTAGE_MIN = 0;
const PERCENTAGE_MAX = 100;

interface PercentageInputProps {
  value: number;
  onChange: (value: number) => void;
  'data-testid'?: string;
}

function PercentageInput({ value, onChange, 'data-testid': dataTestId }: PercentageInputProps) {
  const [inputValue, setInputValue] = useState(String(Math.round(value)));
  const isInternalChangeRef = useRef(false);

  useEffect(() => {
    if (!isInternalChangeRef.current) {
      setInputValue(String(Math.round(value)));
    }
    isInternalChangeRef.current = false;
  }, [value]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInputValue(newValue);
      const numValue = parseFloat(newValue);
      if (!Number.isNaN(numValue) && numValue >= PERCENTAGE_MIN && numValue <= PERCENTAGE_MAX) {
        isInternalChangeRef.current = true;
        onChange(numValue);
      }
    },
    [onChange]
  );

  const handleBlur = useCallback(() => {
    const numValue = parseFloat(inputValue);
    const clamped = Number.isNaN(numValue)
      ? value
      : Math.max(PERCENTAGE_MIN, Math.min(PERCENTAGE_MAX, Math.round(numValue)));
    setInputValue(String(clamped));
    if (clamped !== value) {
      onChange(clamped);
    }
  }, [inputValue, value, onChange]);

  return (
    <div className="flex w-[50px] shrink-0 items-center justify-center rounded-md bg-gray-100 px-2 py-1">
      <input
        type="number"
        min={PERCENTAGE_MIN}
        max={PERCENTAGE_MAX}
        step={1}
        value={inputValue}
        onChange={handleChange}
        onBlur={handleBlur}
        data-testid={dataTestId}
        maxLength={3}
        className="min-w-0 border-none bg-transparent p-0 text-center text-xs font-medium text-gray-700 outline-none ring-0 focus:outline-none focus:ring-0 focus:ring-offset-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
      />
      <span className="text-xs font-medium text-gray-400" aria-hidden="true">
        %
      </span>
    </div>
  );
}

export default PercentageInput;
